<template>
  <div class="mx-auto max-w-6xl" data-testid="page-checkout-order">
    <h1 class="mb-6 txt-headline-2xl-bold">{{ t('FestivalShop.checkout.finishOrder') }}</h1>

    <div v-if="cartItems.length" class="overflow-hidden rounded-lg border border-main bg-surface">
      <div class="grid grid-cols-[2fr_1fr]">
        <div>
          <div v-if="recipientEntries.length" class="border-subtle px-8 pt-8 pb-6">
            <h3 class="text-body-s-bold">{{ t('FestivalShop.checkout.billingRecipientQuestion') }}</h3>
            <div class="space-y-4">
              <RadioButton
                v-for="{ order } in recipientEntries"
                :key="order.id"
                v-model="selectedOrderId"
                :value="order.id"
                :class="order.id === selectedOrderId ? 'border-primary' : 'border-main'"
                class="flex w-full items-start gap-3 rounded-sm border p-4"
              >
                <span>
                  <span class="txt-body-s-bold">{{ formatCompany(order) }}</span>
                  <span class="mt-1 block txt-label-m text-subtle">
                    {{ formatContact(order) }}<br />
                    {{ formatStreet(order) }}
                    <span v-if="order.vatNumber"><br />{{ t('FestivalShop.checkout.vatNumber') }} {{ order.vatNumber }}</span>
                    <span v-if="order.customerReference"
                      ><br />{{ t('FestivalShop.checkout.orderPoNumber') }} {{ order.customerReference }}</span
                    >
                    <span v-if="formatBookingSource(order)"><br />{{ formatBookingSource(order) }}</span>
                  </span>
                </span>
              </RadioButton>
              <RadioButton
                v-model="selectedOrderId"
                :value="null"
                :class="selectedOrderId === null ? 'border-primary' : 'border-main'"
                class="flex w-full items-start gap-3 rounded-sm border p-4"
              >
                <span class="txt-body-s-medium">{{ t('FestivalShop.checkout.newBillingAddress') }}</span>
              </RadioButton>
            </div>
          </div>
          <h3 v-if="!sameKioskSelected" class="my-4 ml-8 txt-headline-m">{{ t('FestivalShop.checkout.billingAddress') }}</h3>
          <BillingAddressForm
            :is-submitting="isSubmitting"
            :order="selectedOrder ?? undefined"
            :is-locked="sameKioskSelected"
            :show-heading="recipientEntries.length === 0"
            @submit="handleSubmit"
          />
        </div>
        <OrderSummary />
      </div>
    </div>

    <div v-else class="rounded-lg border border-main bg-surface p-8 text-center">
      <p class="text-body-l">{{ t('FestivalShop.checkout.orderNotFound') }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { RadioButton } from '@ramp106/omrjs-core-ui'
import { useApolloClient } from '@vue/apollo-composable'
import { computed, inject, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import BillingAddressForm, { type FormData } from '@/components/FestivalShop/BillingAddressForm.vue'
import OrderSummary from '@/components/FestivalShop/OrderSummary.vue'
import { FestivalShopCartKey } from '@/composables/useFestivalShopCart'
import {
  useCreatePaymentInfoMutation,
  useCreateExtrasOrderMutation,
  ExtrasOrdersDocument,
  useExtrasOrdersQuery,
  useUpdateExtrasOrderMutation,
  type Event,
  type ExtraAttributes,
} from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import type { ExtrasOrderNode } from '@/views/FestivalShop'
import { buildBillingRecipients, defaultRecipientId } from '@/views/FestivalShop/checkoutBillingRecipients'

const { t } = useI18n()
const router = useRouter()
const { client } = useApolloClient()

const props = defineProps<{
  eventSlug: string
  kioskId: string
  event?: Event
}>()

const rootEventSlug = computed(() => props.event?.rootEvent?.slug ?? props.eventSlug)

// Initialize mutation hooks
const { mutate: createPaymentInfo } = useCreatePaymentInfoMutation()
const { mutate: createExtrasOrder } = useCreateExtrasOrderMutation()
const { mutate: updateExtrasOrder } = useUpdateExtrasOrderMutation()
const extrasOrdersQueryVariables = computed(() => ({
  rootEvent: { slug: rootEventSlug.value },
  limit: 100,
}))
const currentKioskGlobalId = computed(() => globalizeUrlifiedId(props.kioskId))
const { result: extrasOrdersResult } = useExtrasOrdersQuery(() => extrasOrdersQueryVariables.value)

// Get cart from injected context
const festivalShopCart = inject(FestivalShopCartKey)
if (!festivalShopCart) {
  throw new Error('FestivalShopCart not provided')
}
const { cartItems } = toRefs(festivalShopCart)

const isSubmitting = ref(false)

const extrasOrders = computed<ExtrasOrderNode[]>(() => extrasOrdersResult.value?.extrasOrders?.nodes ?? [])
const recipientEntries = computed(() => buildBillingRecipients(extrasOrders.value, currentKioskGlobalId.value))
const selectedOrderId = ref<string | null>(null)

const selectedEntry = computed(() => recipientEntries.value.find((entry) => entry.order.id === selectedOrderId.value) ?? null)
const selectedOrder = computed(() => selectedEntry.value?.order ?? null)
const sameKioskSelected = computed(() => selectedEntry.value?.sameKiosk === true)

const extrasKey = (productId: string, variantId: string) => {
  return `${productId}:${variantId}`
}

const buildExtrasPayload = (order: ExtrasOrderNode | null): ExtraAttributes[] => {
  const extrasMap = new Map<string, ExtraAttributes>()

  order?.extras.forEach((extra) => {
    const productId = extra.product.id
    const variantId = extra.variant.id
    const key = extrasKey(productId, variantId)

    extrasMap.set(key, {
      productId,
      variantId,
      count: extra.count,
      comment: extra.comment || undefined,
    })
  })

  cartItems.value.forEach((item) => {
    const key = extrasKey(item.productId, item.variantId)
    const existing = extrasMap.get(key)

    if (existing) {
      existing.count += item.count
      existing.comment = item.comment || existing.comment
      existing.files = item.file || existing.files
      return
    }

    extrasMap.set(key, {
      productId: item.productId,
      variantId: item.variantId,
      count: item.count,
      comment: item.comment || undefined,
      files: item.file || undefined,
    })
  })

  return Array.from(extrasMap.values())
}

const formatCompany = (order: ExtrasOrderNode) => {
  return order.company || order.partnerCompany?.name || ''
}

const formatContact = (order: ExtrasOrderNode) => {
  return [order.title, order.firstName, order.lastName].filter(Boolean).join(' ')
}

const formatStreet = (order: ExtrasOrderNode) => {
  const street = [order.street, order.number].filter(Boolean).join(' ')
  const city = [order.zip, order.city].filter(Boolean).join(' ')
  return [street, city].filter(Boolean).join(', ')
}

const formatBookingSource = (order: ExtrasOrderNode) => {
  const kiosk = order.kiosk
  if (!kiosk) return ''
  if (kiosk.__typename === 'Booth') return kiosk.boothNumber || kiosk.name || ''
  return kiosk.name || ''
}

watch(
  recipientEntries,
  (entries) => {
    if (!entries.length) {
      selectedOrderId.value = null
      return
    }
    if (!selectedOrderId.value || !entries.some((entry) => entry.order.id === selectedOrderId.value)) {
      selectedOrderId.value = defaultRecipientId(entries)
    }
  },
  { immediate: true },
)

const handleSubmit = async (formData: FormData) => {
  isSubmitting.value = true

  try {
    if (sameKioskSelected.value && selectedOrderId.value) {
      const extrasOrderResult = await updateExtrasOrder(
        {
          kioskId: globalizeUrlifiedId(props.kioskId),
          rootEvent: { slug: rootEventSlug.value },
          extrasOrderId: selectedOrderId.value,
          extras: buildExtrasPayload(selectedOrder.value),
        },
        {
          refetchQueries: [
            {
              query: ExtrasOrdersDocument,
              variables: extrasOrdersQueryVariables.value,
            },
          ],
          awaitRefetchQueries: true,
        },
      )

      const extrasOrderErrors = extrasOrderResult?.data?.updateExtrasOrder?.errors || []
      if (extrasOrderErrors.length > 0) {
        showNotification(extrasOrderErrors.join(', '), 'error')
        return
      }
    } else {
      const paymentInfoResult = await createPaymentInfo({
        attributes: {
          salutation: formData.salutation,
          title: formData.title || undefined,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          country: formData.country,
          company: formData.company || undefined,
          vatNumber: formData.vatNumber || undefined,
          customerReference: formData.customerReference || undefined,
          zip: formData.zip,
          city: formData.city,
          street: formData.street,
          number: formData.number,
          additionalAddress: formData.additionalAddress || undefined,
          business: !!formData.company,
        },
      })

      const paymentInfoErrors = paymentInfoResult?.data?.createPaymentInfo?.errors || []
      if (paymentInfoErrors.length > 0) {
        showNotification(paymentInfoErrors.join(', '), 'error')
        return
      }

      const paymentInfoId = paymentInfoResult?.data?.createPaymentInfo?.paymentInfo?.id
      if (!paymentInfoId) {
        showNotification(t('FestivalShop.checkout.paymentInfoError'), 'error')
        return
      }

      const extrasOrderResult = await createExtrasOrder(
        {
          kioskId: globalizeUrlifiedId(props.kioskId),
          rootEvent: { slug: rootEventSlug.value },
          paymentInfoId,
          extras: buildExtrasPayload(null),
        },
        {
          refetchQueries: [
            {
              query: ExtrasOrdersDocument,
              variables: extrasOrdersQueryVariables.value,
            },
          ],
        },
      )

      const extrasOrderErrors = extrasOrderResult?.data?.createExtrasOrder?.errors || []
      if (extrasOrderErrors.length > 0) {
        showNotification(extrasOrderErrors.join(', '), 'error')
        return
      }
    }

    client.cache.evict({ fieldName: 'festivalShopProducts' })
    client.cache.evict({ fieldName: 'productById' })
    client.cache.gc()

    // Success - clear cart, show notification and navigate to confirmation
    festivalShopCart.clearCart()
    router.push({
      name: 'shopOrderConfirmation',
      params: {
        eventSlug: props.eventSlug,
        kioskId: props.kioskId,
      },
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  } finally {
    isSubmitting.value = false
  }
}
</script>
